import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Select, Form, Button, Col, Row } from 'antd'
import { useMessageStore } from 'hooks/useGlobalContext/useMessageStore'
import styled from '@emotion/styled'
import { useLazyQuery } from '@apollo/client'
import { FETCH_MENUS, GET_LIGHTSPEED_ORDER_PROFILES } from './queries'
import { LightspeedMenu, LightspeedOrderProfile } from './types'
import { DeleteOutlined } from '@ant-design/icons'
import { CommonButton } from 'components/Common'
import { useStoreIntegration } from './hooks/useStoreIntegration'
import { useLocationPageData } from './hooks/useLocationPageData'
import { usePreconnectStore } from './hooks/usePreconnectStore'
import { PartnerName, PARTNER_SLUGS } from '../../constants'
import { useMerchant } from 'hooks/useGlobalContext/useMerchant'
import CommonHeader from '../Common/CommonHeader'
import Loading from 'components/Utils/Loading'
import {
  Card,
  FormItem,
  FormLabel,
  FormItemTitle,
  FormItemDescription,
  RemoveButton
} from './styles'

type FormValues = {
  storeId: string
  businessLocationId: string
  menuId: string
  delivery: string
  pickup: string
  orderAtTable: string
}

const LIGHTSPEED_PATH = `/integrations/${PARTNER_SLUGS.LIGHTSPEED}`

const LocationPage = () => {
  const navigate = useNavigate()
  const { merchantLocations: stores } = useMerchant()
  const { addMessage } = useMessageStore()
  const { disconnectStore, connectStore, syncMenu } = useStoreIntegration()

  const { selectedBusiness, selectedLocation, connectedLocations, isLoading } =
    useLocationPageData()

  const { preconnectStore } = usePreconnectStore(
    selectedBusiness,
    connectedLocations
  )

  const [form] = Form.useForm()
  const [canSubmit, setCanSubmit] = useState(false)
  const [isFormLoading, setIsFormLoading] = useState(false)

  const [menusData, setMenusData] = useState<LightspeedMenu[]>([])
  const [orderProfilesData, setOrderProfilesData] = useState<
    LightspeedOrderProfile[]
  >([])

  const [
    fetchOrderProfiles,
    { loading: orderProfilesLoading, errors: orderProfilesErrors }
  ] = useLazyQuery<{
    getLightspeedOrderProfiles: LightspeedOrderProfile[]
  }>(GET_LIGHTSPEED_ORDER_PROFILES, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    onCompleted: (data) => {
      setOrderProfilesData(data?.getLightspeedOrderProfiles || [])
    }
  })

  const [fetchMenus, { loading: menusLoading, errors: menusErrors }] =
    useLazyQuery<{ fetchPartnerMenus: LightspeedMenu[] }>(FETCH_MENUS, {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      onCompleted: (data) => {
        setMenusData(data?.fetchPartnerMenus || [])
      }
    })

  useEffect(() => {
    if (menusErrors || orderProfilesErrors) {
      handleError()
    }
  }, [menusErrors, orderProfilesErrors])

  useEffect(() => {
    if (selectedLocation) {
      setIsFormLoading(true)

      const promises = [
        fetchOrderProfiles({
          variables: { storeId: selectedLocation.id }
        }),
        fetchMenus({
          variables: {
            partnerName: PartnerName.LIGHTSPEED,
            storeId: selectedLocation.id
          }
        })
      ]

      Promise.all(promises).then(() => {
        setIsFormLoading(false)
        setCanSubmit(true)
        form.setFieldsValue({
          storeId: selectedLocation?.id,
          businessLocationId:
            selectedLocation?.store_integration_settings
              .lightspeed_business_location_id,
          menuId:
            selectedLocation?.store_integration_settings.lightspeed_menus[0]
              ?.id,
          delivery:
            selectedLocation?.store_integration_settings
              .lightspeed_order_profile_mapping?.delivery || null,
          pickup:
            selectedLocation?.store_integration_settings
              .lightspeed_order_profile_mapping?.pickup || null,
          orderAtTable:
            selectedLocation?.store_integration_settings
              .lightspeed_order_profile_mapping?.orderAtTable || null
        })
      })
    }
  }, [selectedLocation])

  const handleError = () => {
    addMessage({
      type: 'message',
      content: 'Your session has expired. Please log in again.'
    })
    navigate('/integrations')
  }

  const handleChange = async (changedFields: { name: string }[]) => {
    const storeId = form.getFieldValue('storeId')
    const businessLocationId = form.getFieldValue('businessLocationId')
    const menuId = form.getFieldValue('menuId')

    setCanSubmit(Boolean(storeId && businessLocationId && menuId))

    if (!['storeId', 'businessLocationId'].includes(changedFields[0].name[0])) {
      return
    }

    if (!selectedBusiness) return

    const businessLocationName = selectedBusiness.businessLocations.find(
      (location) => location.id === businessLocationId
    )?.name

    if (!storeId || !businessLocationId || !businessLocationName) {
      return
    }

    await preconnectStore({
      storeId,
      businessLocationId,
      businessLocationName
    })

    fetchMenus({
      variables: { partnerName: PartnerName.LIGHTSPEED, storeId }
    })

    fetchOrderProfiles({
      variables: { storeId }
    })
  }

  const handleFinish = async ({
    storeId,
    businessLocationId,
    menuId,
    delivery,
    pickup,
    orderAtTable
  }: FormValues) => {
    if (!selectedBusiness) return

    const businessLocationName = selectedBusiness.businessLocations.find(
      (location) => location.id === businessLocationId
    )?.name

    const menuName = menusData.find((menu) => menu.id === menuId)?.name

    if (!businessLocationName || !menuName) return

    if (selectedLocation && selectedLocation.id !== storeId) {
      disconnectStore({ storeId: selectedLocation.id })
    }

    try {
      await connectStore({
        storeId,
        businessId: selectedBusiness.id,
        businessName: selectedBusiness.name,
        businessLocationId,
        businessLocationName,
        menus: [{ id: menuId, name: menuName }],
        orderProfileMapping: {
          delivery,
          pickup,
          orderAtTable
        }
      })

      addMessage({
        type: 'message',
        content:
          "Your Slerp store and Lightspeed location are now connected. To display your Lightspeed menu on this store, you'll need to sync your menus.",
        duration: 10
      })

      navigate(LIGHTSPEED_PATH)
    } catch (error) {
      addMessage({
        type: 'message',
        content: 'Failed to connect store!'
      })
      handleError()
    }
  }

  const handleRemove = async () => {
    if (!selectedLocation?.id) return

    try {
      await disconnectStore({ storeId: selectedLocation.id })
      addMessage({
        type: 'message',
        content: 'Connection removed successfully!'
      })
      navigate(LIGHTSPEED_PATH)
    } catch (error) {
      addMessage({
        type: 'message',
        content: 'Failed to remove connection!'
      })
      handleError()
    }
  }

  const handleBack = async () => {
    await preconnectStore(null)
    navigate(LIGHTSPEED_PATH)
  }

  const handleSyncMenu = async () => {
    if (!selectedLocation?.id) return

    try {
      await syncMenu(selectedLocation.id)
      addMessage({
        type: 'message',
        content: 'Menu synced successfully!'
      })
      navigate(LIGHTSPEED_PATH)
    } catch (error) {
      addMessage({
        type: 'message',
        content: 'Failed to sync menu!'
      })
      handleError()
    }
  }

  if (isLoading) {
    return <Loading />
  }

  if (!selectedBusiness) {
    return null
  }

  const slerpStoreOptions = stores.map((store) => ({
    label: store.name,
    value: store.id
  }))

  const lightspeedLocationOptions = selectedBusiness.businessLocations.map(
    (location) => ({
      label: location.name,
      value: location.id
    })
  )

  const lightspeedMenuOptions = menusData.map((menu) => ({
    label: menu.name,
    value: menu.id
  }))

  const lightspeedOrderProfileMapped = orderProfilesData.map((profile) => ({
    label: `${profile.code[0].toUpperCase()}${profile.code.slice(1)}`,
    value: profile.code
  }))

  const lightspeedOrderProfileOptions = [
    { label: 'Disabled', value: null },
    ...lightspeedOrderProfileMapped
  ]

  return (
    <Root>
      <CommonHeader
        title='Connect Lightspeed'
        onBack={handleBack}
        helpLink='https://support.slerp.com/knowledge/how-do-i-connect-to-lightspeed-k-series'
      />

      <ContentCard>
        <PageTitle>
          {selectedLocation ? 'Update Store Connection' : 'Connect Store'}
        </PageTitle>
        <PageDescription>
          Map your Slerp store to a Lightspeed store. After mapping is complete,
          choose the Lightspeed menu you want to pull into your Slerp store
          inventory.
        </PageDescription>

        <Form
          onFinish={handleFinish}
          onFieldsChange={handleChange}
          form={form}
          initialValues={{
            delivery: null,
            pickup: null,
            orderAtTable: null
          }}
        >
          <Card isLoading={isFormLoading}>
            <div>
              <FormLabel>
                <FormItemTitle>Slerp Store</FormItemTitle>
                <FormItemDescription>
                  Choose the store to connect to Lightspeed.
                </FormItemDescription>
              </FormLabel>
              <FormItem name='storeId' required>
                <Select
                  placeholder='Select Slerp Store'
                  options={slerpStoreOptions}
                />
              </FormItem>
            </div>

            <div>
              <FormLabel>
                <FormItemTitle>Lightspeed Location</FormItemTitle>
                <FormItemDescription>
                  Changing the Lightspeed store will require you to reselect a
                  menu.
                </FormItemDescription>
              </FormLabel>
              <FormItem name='businessLocationId' required>
                <Select
                  placeholder='Select Lightspeed Location'
                  options={lightspeedLocationOptions}
                />
              </FormItem>
            </div>

            {!!selectedLocation && (
              <RemoveButton type='text' onClick={handleRemove}>
                <span>Delete Connection</span>
                <DeleteOutlined />
              </RemoveButton>
            )}
          </Card>

          <Card gap={8} isLoading={orderProfilesLoading}>
            <div>
              <FormLabel>
                <FormItemTitle>Enable fulfilment types</FormItemTitle>
                <FormItemDescription>
                  To sync a fulfillment type with Lightspeed, map it to an Order
                  Profile. Unmapped types won't be sent to the POS.
                </FormItemDescription>
              </FormLabel>
            </div>

            <div>
              <StyledRow>
                <Col span={12}>
                  <FormItemTitle>Slerp Fulfiment type</FormItemTitle>
                </Col>
                <Col span={12}>
                  <FormItemTitle>Lightspeed order profile</FormItemTitle>
                </Col>
              </StyledRow>
              <StyledRow align='middle'>
                <Col span={12}>
                  <FormItemTitle>Delivery</FormItemTitle>
                </Col>
                <Col span={12}>
                  <FormItem name='delivery'>
                    <Select
                      options={lightspeedOrderProfileOptions}
                      placeholder='Select Delivery Profile'
                    />
                  </FormItem>
                </Col>
              </StyledRow>
              <StyledRow align='middle'>
                <Col span={12}>
                  <FormItemTitle>Pickup</FormItemTitle>
                </Col>
                <Col span={12}>
                  <FormItem name='pickup'>
                    <Select
                      options={lightspeedOrderProfileOptions}
                      placeholder='Select Pickup Profile'
                    />
                  </FormItem>
                </Col>
              </StyledRow>
              <StyledRow align='middle'>
                <Col span={12}>
                  <FormItemTitle>Order at table</FormItemTitle>
                </Col>
                <Col span={12}>
                  <FormItem name='orderAtTable'>
                    <Select
                      options={lightspeedOrderProfileOptions}
                      placeholder='Select Table Order Profile'
                    />
                  </FormItem>
                </Col>
              </StyledRow>
            </div>
          </Card>

          <Card isLoading={menusLoading}>
            <div>
              <FormLabel>
                <FormItemTitle>Select Menu</FormItemTitle>
                <FormItemDescription>
                  This menu will be used as your Slerp inventory.
                </FormItemDescription>
              </FormLabel>
              <FormItem name='menuId' required>
                <Select
                  placeholder='Select Lightspeed Menu'
                  options={lightspeedMenuOptions}
                />
              </FormItem>
            </div>
          </Card>
        </Form>
      </ContentCard>

      <ContentCard padding={12}>
        <Actions>
          {!!selectedLocation && (
            <CommonButton onClick={handleSyncMenu} disabled={!canSubmit}>
              <span>Sync menu</span>
            </CommonButton>
          )}

          <Divider />
          <CommonButton onClick={handleBack}>Cancel</CommonButton>
          <SubmitButton onClick={() => form.submit()} disabled={!canSubmit}>
            {selectedLocation ? 'Update' : 'Save'}
          </SubmitButton>
        </Actions>
      </ContentCard>
    </Root>
  )
}

const Root = styled.div`
  display: flex;
  gap: 24px;
  flex-direction: column;
`

const Divider = styled.div`
  flex-grow: 1;
`

const ContentCard = styled.div<{ padding?: number }>(
  ({ padding = 24 }) => `
  margin: 0 auto;
  width: 100%;
  max-width: 640px;
  padding: ${padding}px 24px;
  background-color: #fff;
  border-radius: 6px;
`
)

const PageTitle = styled.h2`
  font-size: 16px;
  font-weight: 600;
  color: #4a4a68;
`

const PageDescription = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: #4a4a68;
  margin-bottom: 24px;
`

const StyledRow = styled(Row)`
  margin-bottom: 6px;
`

const Actions = styled.div`
  display: flex;
  gap: 16px;

  > button {
    width: fit-content;
    min-width: 80px;
    max-width: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`

const SubmitButton = styled(Button)`
  width: 100%;
`

export default LocationPage
